import { getLogger } from '@logtape/logtape';
import isPostalCode, { type PostalCodeLocale } from 'validator/es/lib/isPostalCode';
import { email, z } from 'zod';

import { isLandlinePhoneNumber, isMobilePhoneNumber } from '@/utils/patientUtils.ts';

const logger = getLogger(['schema', 'patient-data-form']);

const PHONE_PLACEHOLDER = '+49';

// The '+49' placeholder and empty values are treated as "not provided" and skip validation.
const validatePhoneField = (
    ctx: z.RefinementCtx,
    value: string | undefined,
    country_code: string,
    validator: (phone: string, country_code?: string) => boolean,
    path: 'phone' | 'landline_phone',
    message: string
): void => {
    if (!value || value === PHONE_PLACEHOLDER) return;
    if (!validator(value, country_code)) {
        ctx.addIssue({ code: 'custom', message: message, path: [path] });
    }
};

export const patient_data_form_schema = z
    .object({
        academic_title: z.string().optional(),
        salutation: z.string().optional(),
        gender: z.string().optional(),
        street: z.string().optional(),
        house_number: z.string().optional(),
        addon: z.string().optional(),
        email: z.union([z.literal(''), email('Deine E-Mail ist ungueltig.')]).optional(),
        phone: z.string().optional(),
        landline_phone: z.string().optional(),
        postal_code: z.string(),
        country_code: z.string(),
    })
    // Validate phone numbers (country-aware) and postal code
    .superRefine((data, ctx) => {
        validatePhoneField(
            ctx,
            data.phone,
            data.country_code,
            isMobilePhoneNumber,
            'phone',
            'Ungültige Telefonnummer, muss eine gültige Mobiltelefonnummer sein (z.B. +49151... oder 0151...)'
        );
        validatePhoneField(
            ctx,
            data.landline_phone,
            data.country_code,
            isLandlinePhoneNumber,
            'landline_phone',
            'Ungültige Telefonnummer, muss eine gültige Festnetznummer sein (z.B. +4930... oder 030...)'
        );

        // Skip if postal code or country code is empty
        if (!data.postal_code || !data.country_code) return;

        // Validate postal code for the specific country
        let is_valid: boolean;
        try {
            is_valid = isPostalCode(data.postal_code, data.country_code.toUpperCase() as PostalCodeLocale);
        } catch (error) {
            logger.warn('Postal code validation failed for locale {countryCode}', {
                countryCode: data.country_code,
                error: error,
            });
            // Fallback validation: if postal code is longer than 2 characters, consider it valid
            is_valid = data.postal_code.length > 2;
        }

        if (!is_valid) {
            ctx.addIssue({
                code: 'custom',
                message: 'Ungültige Postleitzahl',
                path: ['postal_code'],
            });
        }
    });
